import { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { userService } from '../services';
import { trackingEvent } from '../analytics/index';
import { AMPLITUDE_SERVICE } from '../analytics/constants';
import { getBasicAmplitudEventProperties } from '../analytics/utils';
import {
  fetchResponsablesRequested,
  fetchResponsablesSucceeded,
  fetchResponsablesFailed,
  getAllResponsablesSelector,
  getResponsablesLoadingSelector,
  getResponsablesErrorSelector,
  addResponsable,
  updateResponsable,
  removeResponsable,
  clearResponsables
} from '../redux/slices/responsableSlice';

/**
 * Custom hook for managing responsables with Redux integration
 * Handles fetching, creating, updating, and deleting responsables
 */
export const useResponsables = () => {
  const dispatch = useDispatch();

  // Redux state
  const responsablesFromRedux = useSelector(getAllResponsablesSelector);
  const loadingFromRedux = useSelector(getResponsablesLoadingSelector);
  const errorFromRedux = useSelector(getResponsablesErrorSelector);

  // Local state for additional functionality
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  /**
   * Fetches responsables by sector and stores them in Redux
   */
  const fetchResponsablesBySector = useCallback(
    async (sectorId, setToSelectResponsables = null) => {
      if (!sectorId) {
        const error = 'No sector selected';
        setLocalError(error);
        dispatch(fetchResponsablesFailed(error));
        return null;
      }

      dispatch(fetchResponsablesRequested());
      setLocalLoading(true);
      setLocalError(null);

      try {
        const response = await userService.getBySector(sectorId);

        if (!response?.users) {
          const error = 'No responsables found';
          setLocalError(error);
          dispatch(fetchResponsablesFailed(error));
          return [];
        }

        // Dispatch to Redux store
        dispatch(fetchResponsablesSucceeded(response.users));

        // Call external setter if provided (for backward compatibility)
        if (setToSelectResponsables) {
          setToSelectResponsables(response.users);
        }

        // Handle gantt integration if needed
        const gantt = window.to_use_react_gantt;
        if (gantt && response.users?.length > 0) {
          gantt.toSelectResponsables = response.users.filter(
            (option) => option.is_active !== false
          );
        } else {
          gantt.toSelectResponsables = response.users;
        }

        return response.users;
      } catch (err) {
        const errorMessage = err.message || 'Failed to fetch responsables';
        setLocalError(errorMessage);
        dispatch(fetchResponsablesFailed(errorMessage));
        return null;
      } finally {
        setLocalLoading(false);
      }
    },
    [dispatch]
  );

  /**
   * Creates a new responsable
   */
  const createResponsable = useCallback(
    async (responsableData) => {
      setLocalLoading(true);
      setLocalError(null);

      try {
        const response = await userService.create(responsableData);

        if (response) {
          // Add to Redux store
          dispatch(addResponsable(response));

          // Track the event
          trackingEvent(
            'responsable_created',
            {
              ...getBasicAmplitudEventProperties(),
              responsable_name: response.name,
              responsable_id: response.id,
              sector_id: responsableData.sectorId
            },
            AMPLITUDE_SERVICE
          );

          return response;
        }

        return null;
      } catch (err) {
        const errorMessage = err.message || 'Failed to create responsable';
        setLocalError(errorMessage);
        return null;
      } finally {
        setLocalLoading(false);
      }
    },
    [dispatch]
  );

  /**
   * Updates an existing responsable
   */
  const updateResponsableById = useCallback(
    async (responsableId, responsableData) => {
      setLocalLoading(true);
      setLocalError(null);

      try {
        const response = await userService.update({
          id: responsableId,
          ...responsableData
        });

        if (response) {
          // Update in Redux store
          dispatch(updateResponsable(response));

          // Track the event
          trackingEvent(
            'responsable_updated',
            {
              ...getBasicAmplitudEventProperties(),
              responsable_name: response.name,
              responsable_id: response.id
            },
            AMPLITUDE_SERVICE
          );

          return response;
        }

        return null;
      } catch (err) {
        const errorMessage = err.message || 'Failed to update responsable';
        setLocalError(errorMessage);
        return null;
      } finally {
        setLocalLoading(false);
      }
    },
    [dispatch]
  );

  /**
   * Deletes a responsable
   */
  const deleteResponsable = useCallback(
    async (responsableId) => {
      setLocalLoading(true);
      setLocalError(null);

      try {
        const response = await userService.delete(responsableId);

        if (response) {
          // Remove from Redux store
          dispatch(removeResponsable(responsableId));

          // Track the event
          trackingEvent(
            'responsable_deleted',
            {
              ...getBasicAmplitudEventProperties(),
              responsable_id: responsableId
            },
            AMPLITUDE_SERVICE
          );

          return true;
        }

        return false;
      } catch (err) {
        const errorMessage = err.message || 'Failed to delete responsable';
        setLocalError(errorMessage);
        return false;
      } finally {
        setLocalLoading(false);
      }
    },
    [dispatch]
  );

  /**
   * Clears all responsables from Redux store
   */
  const clearAllResponsables = useCallback(() => {
    dispatch(clearResponsables());
  }, [dispatch]);

  /**
   * Gets a responsable by ID from Redux store
   */
  const getResponsableById = useCallback(
    (responsableId) =>
      responsablesFromRedux.find(
        (responsable) => responsable.id === responsableId
      ),
    [responsablesFromRedux]
  );

  /**
   * Refreshes responsables for current sector
   */
  const refreshResponsables = useCallback(
    (sectorId) => fetchResponsablesBySector(sectorId),
    [fetchResponsablesBySector]
  );

  return {
    // Data
    responsables: responsablesFromRedux,
    loading: loadingFromRedux || localLoading,
    error: errorFromRedux || localError,

    // Actions
    fetchResponsablesBySector,
    createResponsable,
    updateResponsable: updateResponsableById,
    deleteResponsable,
    clearAllResponsables,
    refreshResponsables,

    // Utilities
    getResponsableById
  };
};
